import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { formatApiErrorDetail } from '../utils/api';
import { Heart, Gift, Check, ArrowRight, ArrowLeft,
  MessageSquare, ExternalLink, PartyPopper, HelpCircle, Shield, MessageCircle
} from 'lucide-react';
import { BackToHome } from '../components/BackToHome';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { FAQEmbed } from '../components/FAQEmbed';
import { Tx } from '../components/EditMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Currency-aware preset amounts.
// These are the ROUNDED native-currency values shown on the chips and saved as `pledge_amount`.
// We also save `pledge_currency` so admins can see what the supporter intended.
const PLEDGE_PRESETS = {
  NOK: [250, 500, 1000],
  GBP: [15, 25, 50],
  EUR: [25, 50, 100],
  USD: [15, 25, 50],
};
const CURRENCY_SYMBOLS = { NOK: 'NOK', GBP: '£', EUR: '€', USD: '$' };

// Map IP country → most natural currency for that visitor.
// Norway/Scandinavia → NOK. UK → GBP. USA → USD. Most of EU → EUR.
const COUNTRY_TO_CURRENCY = {
  NO: 'NOK', SE: 'NOK', DK: 'NOK', IS: 'NOK',  // Scandinavia → NOK (closest)
  GB: 'GBP', UK: 'GBP',
  US: 'USD',
  // Default for any EU country falls through to EUR below
};
const EU_COUNTRIES = new Set([
  'AT','BE','BG','HR','CY','CZ','DE','EE','ES','FI','FR','GR','HU','IE',
  'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI'
]);
const pickCurrencyFromCountry = (country, lang) => {
  if (!country) return lang === 'no' ? 'NOK' : 'GBP';
  const code = country.toUpperCase();
  if (COUNTRY_TO_CURRENCY[code]) return COUNTRY_TO_CURRENCY[code];
  if (EU_COUNTRIES.has(code)) return 'EUR';
  return lang === 'no' ? 'NOK' : 'GBP';
};

export const SignupPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, lang } = useI18n();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [wycliffeLinks, setWycliffeLinks] = useState([]);
  const [whyOpen, setWhyOpen] = useState(false);
  const [publicSettings, setPublicSettings] = useState({});
  const [geoCountry, setGeoCountry] = useState('');

  // Single-choice support path: '' | 'ext_support' | 'in_app_monthly'
  const [supportPath, setSupportPath] = useState('');

  const [pledgeAmount, setPledgeAmount] = useState('');
  const [pledgeStart, setPledgeStart] = useState('apr_2027'); // apr_2027 | y2028 (no "now" — see iteration 12 spec)
  const [pledgeCurrencyOverride, setPledgeCurrencyOverride] = useState(''); // user can switch
  const [message, setMessage] = useState('');

  const [account, setAccount] = useState({
    name: '', email: '', country: '',
    whatsapp_number: '',
    newsletter_subscribed: true, // pre-checked
  });

  useEffect(() => {
    axios.get(`${API}/external-links`).then(r => setWycliffeLinks(r.data)).catch(() => {});
    axios.get(`${API}/settings/public`).then(r => setPublicSettings(r.data)).catch(() => {});
    axios.get(`${API}/geo/lang`).then(r => setGeoCountry(r.data?.country || '')).catch(() => {});
  }, []);

  const autoCurrency = pickCurrencyFromCountry(geoCountry, lang);
  const activeCurrency = (PLEDGE_PRESETS[pledgeCurrencyOverride] ? pledgeCurrencyOverride : autoCurrency);
  const presets = PLEDGE_PRESETS[activeCurrency];
  const symbol = CURRENCY_SYMBOLS[activeCurrency];

  const hasInAppFinancial = supportPath === 'in_app_monthly';
  const hasExternal = supportPath === 'ext_support';
  const anySelected = !!supportPath;

  const choosePath = (path) => {
    // Toggle off if already selected, else lock to that path (mutually exclusive)
    setSupportPath(prev => prev === path ? '' : path);
    if (path !== 'in_app_monthly') setPledgeAmount('');
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    setError('');
    if (!anySelected) {
      setError('Please pick one way you\'d like to walk with us');
      return;
    }
    if (hasInAppFinancial && (!pledgeAmount || parseFloat(pledgeAmount) <= 0)) {
      setError('Please enter your pledge amount');
      return;
    }
    // Step 2 collects everyone's details (name, email, optional newsletter+message+whatsapp)
    setStep(2);
  };

  const submitAccount = async (e) => {
    e.preventDefault();
    setError('');
    if (!account.name.trim() || !account.email.trim()) {
      setError('Please enter your name and email');
      return;
    }
    setLoading(true);
    try {
      // Determine role from supportPath + newsletter checkbox.
      // - in_app_monthly  => financial (monthly pledge)
      // - ext_support     => prayer-only (Wycliffe handles money externally; we record their intent + newsletter)
      // - newsletter      => merges into 'prayer' for ext_support, or upgrades financial to 'both'
      let supporter_role;
      let financial_type = null;
      if (hasInAppFinancial) {
        supporter_role = account.newsletter_subscribed ? 'both' : 'financial';
        financial_type = 'monthly';
      } else {
        // hasExternal — money flows via Wycliffe externally; we save them as prayer/newsletter follower.
        supporter_role = 'prayer';
      }

      // Passwordless registration — no password passed.
      await register(account.name, account.email, undefined, account.country);
      await axios.post(
        `${API}/supporter/setup`,
        {
          supporter_role,
          financial_type,
          pledge_amount: hasInAppFinancial ? parseFloat(pledgeAmount) : 0,
          pledge_currency: hasInAppFinancial ? activeCurrency : 'NOK',
          pledge_start: hasInAppFinancial ? pledgeStart : '',
          whatsapp_number: account.whatsapp_number,
          communication_preference: account.whatsapp_number ? 'both' : 'email',
          newsletter_subscribed: !!account.newsletter_subscribed,
          message,
        },
        { withCredentials: true }
      );
      setStep(3);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const PartnershipOption = ({ checked, onChange, icon: Icon, title, desc, testid, variant = 'default' }) => {
    // variant: 'hero' (amber spotlight — monthly), 'wycliffe' (slate solid — one-time), 'soft' (neutral — prayer)
    const styles = {
      hero: {
        base: 'border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100/40 hover:border-amber-400 hover:shadow-amber-200/50',
        active: 'border-amber-500 bg-gradient-to-br from-amber-100 via-orange-100 to-amber-50 shadow-xl shadow-amber-200/60 ring-4 ring-amber-200/50',
        iconBg: 'bg-amber-500',
        iconBgActive: 'bg-amber-600',
        iconColor: 'text-white',
        titleSize: 'text-lg sm:text-xl',
        ribbon: 'Most needed',
      },
      wycliffe: {
        base: 'border-slate-200 bg-white hover:border-slate-400',
        active: 'border-slate-900 bg-slate-50 shadow-lg',
        iconBg: 'bg-slate-700',
        iconBgActive: 'bg-slate-900',
        iconColor: 'text-white',
        titleSize: 'text-base sm:text-lg',
      },
      soft: {
        base: 'border-slate-200 bg-white hover:border-violet-300',
        active: 'border-violet-500 bg-violet-50 shadow-md',
        iconBg: 'bg-violet-100',
        iconBgActive: 'bg-violet-200',
        iconColor: 'text-violet-700',
        titleSize: 'text-base sm:text-lg',
      },
    };
    const v = styles[variant] || styles.soft;
    return (
      <button data-testid={testid} type="button" onClick={onChange}
              className={`relative text-left p-5 sm:p-6 rounded-2xl border-2 transition-all duration-200 w-full transform ${
                checked ? `${v.active} scale-[1.01]` : v.base
              }`}>
        {variant === 'hero' && v.ribbon && !checked && (
          <span className="absolute -top-3 right-5 text-[10px] uppercase font-bold tracking-wider bg-amber-500 text-white px-3 py-1 rounded-full shadow-md">
            {v.ribbon}
          </span>
        )}
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl flex-shrink-0 transition-colors ${checked ? v.iconBgActive : v.iconBg}`}>
            <Icon className={`w-5 h-5 ${v.iconColor}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-bold text-slate-900 font-['Outfit'] ${v.titleSize}`}>{title}</h3>
              {checked && <Check className="w-5 h-5 text-emerald-600" />}
            </div>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{desc}</p>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 py-12 px-4">
      <LanguageSwitcher variant="floating" />
      <BackToHome />
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((n, i) => (
              <React.Fragment key={n}>
                {i > 0 && (
                  <div className={`w-12 h-1 rounded-full transition-all ${step >= n ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
                )}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                  step >= n ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > n ? <Check className="w-5 h-5" /> : n}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* STEP 1: Multi-select partnership */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 font-['Outfit'] tracking-tight">
              <Tx k="signup.title" />
            </h1>
            <p className="text-slate-600 mb-8 font-['Manrope'] leading-relaxed">
              <Tx k="signup.subtitle" as="span" />
            </p>

            <form data-testid="signup-step1-form" onSubmit={handleStep1Submit} className="space-y-6">
              {error && (
                <div data-testid="signup-error" className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
              )}

              {/* TOP: One-time gift via Wycliffe */}
              <div data-testid="card-external">
                <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="inline-block w-6 h-px bg-slate-500"></span>
                  <Tx k="signup.section.external" />
                </p>
                <PartnershipOption
                  testid="partner-option-ext-support"
                  checked={hasExternal} onChange={() => choosePath('ext_support')}
                  icon={Gift} variant="wycliffe"
                  title={<Tx k="signup.option.external.title" />}
                  desc={<Tx k="signup.option.external.desc" />}
                />
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 pl-1 text-sm">
                  <button
                    type="button"
                    data-testid="external-why-safe-btn"
                    onClick={() => setWhyOpen(true)}
                    className="inline-flex items-center gap-1.5 text-slate-700 hover:text-amber-700 font-medium underline decoration-dotted underline-offset-4"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    {t('signup.external.why_safe')}
                  </button>
                  <Link
                    to="/faq"
                    data-testid="external-faq-link"
                    className="inline-flex items-center gap-1.5 text-slate-600 hover:text-amber-700 font-medium"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                    {t('signup.read_faq')}
                  </Link>
                </div>

                {/* Inline Wycliffe giving links — visible the moment the Wycliffe card is selected.
                    Lets supporters give immediately without stepping through the rest of the form. */}
                {hasExternal && wycliffeLinks.length > 0 && (
                  <div data-testid="inline-wycliffe-links"
                       className="mt-4 p-4 sm:p-5 rounded-2xl bg-slate-50 border border-slate-200">
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                      Wycliffe giving — pick your country
                    </p>
                    <div className="space-y-2">
                      {wycliffeLinks.map((link) => (
                        <a
                          key={link.id}
                          data-testid={`inline-wycliffe-link-${link.id}`}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between gap-3 px-4 py-3 bg-white rounded-xl border border-slate-200 hover:border-slate-400 hover:shadow-sm group transition-all"
                        >
                          <div>
                            <div className="font-semibold text-slate-900 text-sm">{link.source_name}</div>
                            {link.note && (
                              <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{link.note}</div>
                            )}
                          </div>
                          <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-amber-600 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-3 italic">
                      Tap a link to open Wycliffe's giving page. You can also continue below to let us know
                      you gave — so we can track it and say thank you.
                    </p>
                  </div>
                )}
              </div>

              {/* The two options are mutually exclusive — show OR between them */}
              <div className="relative flex items-center justify-center py-1" aria-hidden="true">
                <span className="absolute inset-x-0 top-1/2 h-px bg-slate-200" />
                <span className="relative bg-white px-4 text-xs font-bold tracking-widest text-slate-500 uppercase">
                  {t('signup.section.or')}
                </span>
              </div>

              {/* MIDDLE: Monthly in-app pledge (kickstarter-style promise) */}
              <div data-testid="card-monthly">
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="inline-block w-6 h-px bg-amber-500"></span>
                  <Tx k="signup.section.in_app" />
                </p>
                <PartnershipOption
                  testid="partner-option-inapp-monthly"
                  checked={hasInAppFinancial} onChange={() => choosePath('in_app_monthly')}
                  icon={Heart} variant="hero"
                  title={<Tx k="signup.option.monthly.title" />}
                  desc={<Tx k="signup.option.monthly.desc" />}
                />
                <p className="text-xs text-amber-700 font-semibold italic mt-2 pl-1">
                  <Tx k="signup.monthly.tagline" />
                </p>
              </div>

              {hasInAppFinancial && (
                <div data-testid="financial-amount-section"
                     className="relative p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100/40 border-2 border-amber-200 shadow-lg shadow-amber-200/30">
                  <div className="absolute -top-3 left-6 bg-amber-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow">
                    {t('signup.pledge_amount.ribbon')}
                  </div>
                  <label className="block text-sm font-semibold text-slate-800 mb-1 mt-1">
                    {t('signup.pledge_amount.label', { currency: activeCurrency })}
                  </label>
                  <p className="text-xs text-slate-600 mb-3">
                    {t('signup.pledge_amount.subtitle')}
                  </p>
                  {/* Tiny currency switcher — auto-detected, but supporter can override */}
                  <div className="flex items-center gap-1 mb-4 text-xs">
                    <span className="text-slate-500 mr-1">{t('signup.pledge_amount.currency')}:</span>
                    {Object.keys(PLEDGE_PRESETS).map(code => (
                      <button
                        key={code}
                        type="button"
                        data-testid={`pledge-currency-${code}`}
                        onClick={() => { setPledgeCurrencyOverride(code); setPledgeAmount(''); }}
                        className={`px-2.5 py-1 rounded-full font-semibold transition-all ${
                          activeCurrency === code
                            ? 'bg-amber-500 text-white'
                            : 'bg-white text-slate-600 hover:bg-amber-100'
                        }`}
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <span className="absolute left-5 top-1/2 -translate-y-1/2 text-2xl sm:text-3xl font-bold text-amber-600 font-['Outfit'] pointer-events-none">
                      {symbol}
                    </span>
                    <input
                      data-testid="signup-pledge-amount-input"
                      type="number" required min="1" step="0.01"
                      value={pledgeAmount}
                      onChange={(e) => setPledgeAmount(e.target.value)}
                      className={`w-full ${activeCurrency === 'NOK' ? 'pl-24' : 'pl-14'} pr-5 py-5 rounded-2xl border-2 border-amber-300 bg-white text-3xl sm:text-4xl font-bold text-slate-900 font-['Outfit'] placeholder:text-amber-300/70 placeholder:font-normal focus:outline-none focus:ring-4 focus:ring-amber-300/60 focus:border-amber-500 transition-all shadow-inner`}
                      placeholder="0"
                    />
                    <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 uppercase tracking-wider pointer-events-none">
                      {t('signup.pledge_amount.per_month')}
                    </span>
                  </div>

                  {/* Start date radio — only future-start options now */}
                  <div className="mt-5 pt-5 border-t border-amber-200">
                    <p className="text-sm font-semibold text-slate-800 mb-3">{t('signup.pledge_amount.start')}</p>
                    <div className="grid gap-2">
                      {[
                        { id: 'apr_2027', label: t('signup.pledge_amount.start_2027') },
                        { id: 'y2028',    label: t('signup.pledge_amount.start_2028') },
                      ].map(opt => (
                        <label
                          key={opt.id}
                          data-testid={`pledge-start-${opt.id}`}
                          className={`flex items-center gap-3 cursor-pointer px-4 py-3 rounded-xl border-2 transition-all ${
                            pledgeStart === opt.id
                              ? 'border-amber-500 bg-white shadow-sm'
                              : 'border-transparent bg-white/50 hover:bg-white'
                          }`}
                        >
                          <input
                            type="radio"
                            name="pledge_start"
                            value={opt.id}
                            checked={pledgeStart === opt.id}
                            onChange={() => setPledgeStart(opt.id)}
                            className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm text-slate-800">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* (The newsletter & WhatsApp options now live on step 2 — see below) */}

              <button data-testid="signup-next-btn" type="submit" disabled={!anySelected}
                      className="group relative w-full flex items-center justify-center gap-2 px-6 py-5 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 bg-[length:200%_100%] text-white text-lg rounded-full font-bold tracking-wide hover:bg-[position:100%_0] transition-all duration-500 shadow-lg shadow-amber-500/40 hover:shadow-2xl hover:shadow-amber-500/50 hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none font-['Outfit']">
                <span>{t('signup.next.continue')}</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>

              <p className="text-center text-sm text-slate-600">
                {t('signup.have_account')}{' '}
                <Link to="/login" data-testid="link-to-login" className="text-blue-600 hover:text-blue-700 font-medium">{t('signup.sign_in')}</Link>
              </p>
            </form>

            {/* FAQ preview inline so visitors can browse answers without leaving */}
            <div className="mt-12 pt-8 border-t border-slate-100">
              <FAQEmbed limit={3} />
            </div>
          </div>
        )}

        {/* "Why is this safe?" modal */}
        {whyOpen && (
          <div
            data-testid="why-safe-modal"
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setWhyOpen(false)}
          >
            <div
              className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-emerald-100 flex-shrink-0">
                  <Shield className="w-6 h-6 text-emerald-700" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900 font-['Outfit'] mb-2">
                    Your one-time gift is held safely
                  </h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Your gift goes to <strong>Wycliffe Norway</strong>, held in our names toward our training costs.
                    It becomes available to us through Wycliffe when the time comes to go.
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed mt-3">
                    In the unlikely event we do not step into full-time mission, the money already given is redirected by
                    Wycliffe Norway to one of their exciting, worthwhile, live mission projects. It always serves Bible
                    translation. <strong>Nothing is wasted.</strong>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-slate-100">
                <Link
                  to="/faq"
                  data-testid="why-safe-faq-link"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
                >
                  <HelpCircle className="w-4 h-4" />
                  Read the full FAQ
                </Link>
                <button
                  data-testid="why-safe-close"
                  type="button"
                  onClick={() => setWhyOpen(false)}
                  className="px-5 py-2.5 rounded-full text-slate-700 hover:bg-slate-100 text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Account details (only if in-app option selected) */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 font-['Outfit'] tracking-tight">
              <Tx k="signup.account.title" />
            </h2>
            <p className="text-slate-600 mb-8 font-['Manrope'] leading-relaxed">
              <Tx k="signup.account.subtitle" />
            </p>

            <form data-testid="signup-step2-form" onSubmit={submitAccount} className="space-y-5">
              {error && (
                <div data-testid="signup-step2-error" className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('signup.account.name')}</label>
                <input data-testid="signup-name-input" type="text" required
                       value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })}
                       className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                       placeholder="" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('signup.account.email')}</label>
                <input data-testid="signup-email-input" type="email" required
                       value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })}
                       className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                       placeholder="you@example.com" />
                <p className="text-xs text-slate-500 mt-2">
                  We'll email you a one-click sign-in link whenever you want to log in — no password to remember.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('signup.account.country')}</label>
                  <input data-testid="signup-country-input" type="text"
                         value={account.country} onChange={(e) => setAccount({ ...account, country: e.target.value })}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                         placeholder={t('signup.account.country_placeholder')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('signup.account.whatsapp')}</label>
                  <input data-testid="signup-whatsapp-input" type="tel"
                         value={account.whatsapp_number} onChange={(e) => setAccount({ ...account, whatsapp_number: e.target.value })}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                         placeholder="+47 123 45 678" />
                  <p className="text-xs text-slate-500 mt-1.5">{t('signup.account.whatsapp_hint')}</p>
                </div>
              </div>

              {/* Newsletter checkbox — pre-checked, optional opt-out */}
              <label
                data-testid="signup-newsletter-label"
                className="flex items-start gap-3 p-4 rounded-2xl bg-violet-50/60 border border-violet-100 cursor-pointer hover:bg-violet-50 transition-colors"
              >
                <input
                  type="checkbox"
                  data-testid="signup-newsletter-checkbox"
                  checked={account.newsletter_subscribed}
                  onChange={(e) => setAccount({ ...account, newsletter_subscribed: e.target.checked })}
                  className="mt-0.5 w-5 h-5 rounded text-violet-600 focus:ring-violet-500 accent-violet-600"
                />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-slate-900">{t('signup.account.newsletter')}</span>
                  <span className="block text-xs text-slate-600 mt-0.5">{t('signup.account.newsletter_hint')}</span>
                </span>
              </label>

              {/* Optional message — universal, asks supporters to share thoughts (and Wycliffe amounts if they wish) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4" /> {t('signup.message.label')}
                </label>
                <textarea
                  data-testid="signup-message-input"
                  rows="3"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                  placeholder={t('signup.message.placeholder')}
                />
                <p className="text-xs text-slate-500 mt-1.5">{t('signup.message.hint')}</p>
              </div>

              <div className="flex gap-3">
                <button data-testid="signup-back-btn" type="button" onClick={() => setStep(1)}
                        className="flex items-center justify-center gap-2 px-6 py-4 border border-slate-200 text-slate-700 rounded-full font-medium hover:bg-slate-50">
                  <ArrowLeft className="w-5 h-5" /> <Tx k="signup.back" />
                </button>
                <button data-testid="signup-submit-btn" type="submit" disabled={loading}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? <Tx k="signup.account.submitting" /> : <Tx k="signup.account.submit" />}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* STEP 3: Success + Wycliffe Links */}
        {step === 3 && (
          <div data-testid="signup-success" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PartyPopper className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 font-['Outfit'] tracking-tight">
                <Tx k="signup.success.title" />
              </h2>
              <p className="text-slate-600 font-['Manrope']">
                <Tx k="signup.success.body" />
              </p>
            </div>

            {hasExternal && wycliffeLinks.length > 0 && (
              <div data-testid="success-wycliffe-section" className="mb-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 font-['Outfit']">
                  <Tx k="signup.success.external_title" />
                </h3>
                <div className="space-y-3">
                  {wycliffeLinks.map((link, i) => (
                    <a key={link.id} data-testid={`success-wycliffe-link-${i}`}
                       href={link.url} target="_blank" rel="noopener noreferrer"
                       className="block p-5 border-2 border-slate-200 rounded-2xl hover:border-blue-300 hover:bg-blue-50 transition-all group">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-base font-semibold text-slate-800 group-hover:text-blue-600">{link.source_name}</span>
                        <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                      </div>
                      {link.note && (
                        <p className="text-sm text-slate-600 leading-relaxed">{link.note}</p>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button data-testid="goto-dashboard-btn" onClick={() => navigate('/account')}
                      className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700">
                <Tx k="signup.success.go_dashboard" /> <ArrowRight className="w-4 h-4" />
              </button>
              <button data-testid="goto-home-btn" onClick={() => navigate('/')}
                      className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-700 rounded-full font-medium hover:bg-slate-50">
                <Tx k="signup.success.go_home" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
